type TurnstileRenderOptions = {
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  sitekey: string;
};

type TurnstileApi = {
  remove: (widgetId: string) => void;
  render: (
    container: HTMLElement | string,
    options: TurnstileRenderOptions
  ) => string;
  reset: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SITEKEY = process.env.CVNA_APP_TURNSTILE_SITEKEY;
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error('Failed to load Turnstile script'));
      };
      script.onload = () => resolve();
      script.src = SCRIPT_SRC;
      document.head.appendChild(script);
    });
  }

  return scriptPromise;
}

export async function getValidationToken(): Promise<string> {
  if (!SITEKEY) throw new Error('Turnstile sitekey is not configured');

  await loadTurnstileScript();
  if (!window.turnstile) throw new Error('Turnstile failed to initialize');

  const { turnstile } = window;
  const container = document.createElement('div');
  container.style.cssText =
    'position:fixed;bottom:16px;right:16px;z-index:9999';
  document.body.appendChild(container);

  return new Promise<string>((resolve, reject) => {
    const cleanup = (widgetId: string) => {
      turnstile.remove(widgetId);
      container.remove();
    };
    const widgetId = turnstile.render(container, {
      callback: token => {
        cleanup(widgetId);
        resolve(token);
      },
      'error-callback': () => {
        cleanup(widgetId);
        reject(new Error('Turnstile challenge failed'));
      },
      'expired-callback': () => {
        cleanup(widgetId);
        reject(new Error('Turnstile token expired before submission'));
      },
      sitekey: SITEKEY
    });
  });
}
